/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';

import { Grid, Icon } from '@mairistem/composants';
import { AdresseForm } from '@mairistem/adresses';

import type { Adresse, ListeRecensement } from '@/types';

import { GridColumnDivider, Section, SectionHeader } from '@/components/Common';
import { CoordonneesContacts } from '@mairistem/administres-utils';
import { formatAdresse } from '@/utils';

interface Props {
  tel1: string;
  tel2: string;
  mail: string;
  adresseContact: Partial<Adresse>;
  adresseRattachement: Partial<Adresse>;
  recensement: ListeRecensement;
  errors: Record<string, string>;
  onChange: (
    event: React.ChangeEvent<HTMLInputElement>,
    object: object,
  ) => void;
}

const AdresseContact = (props: Props) => {
  const {
    tel1,
    tel2,
    mail,
    adresseContact,
    adresseRattachement,
    recensement,
    errors,
    onChange,
  } = props;

  const handleItemChange = ({ adresse }) => {
    onChange(null, {
      name: 'adresseContact',
      value: formatAdresse(adresseContact, { ...adresse }),
    });
  };

  const handleAdresseChange = ({ target: { name, value } }) => {
    if (name === 'adresse.type') {
      onChange(null, {
        name: 'adresseContact',
        value: { type: value },
      });
    } else {
      // Allows to change every information in case we're not in type commune
      const res = _.replace(name, 'adresse.', '');
      onChange(null, {
        name: 'adresseContact',
        value: formatAdresse(adresseContact, {
          [res]: value,
        }),
      });
    }
  };

  const handlePasteAdresseRattachement = () => {
    onChange(null, {
      name: 'adresseContact',
      value: {
        ...formatAdresse({}, adresseRattachement),
        type: 'Commune',
      },
    });
  };

  return (
    <Section title="INFORMATIONS DE CONTACT">
      <Grid padded>
        <Grid.Column
          mobile={16}
          computer={9}
          className="grid-column-adresse-contact"
        >
          {/** title avec le sectionHeader et l'icon pour que ce soit aligné avec les types */}
          <AdresseForm
            title={
              <div style={{ display: 'flex', gap: '1em' }}>
                <SectionHeader title="Adresse de contact" />
                <Icon
                  link
                  name="paste"
                  color="blue"
                  data-testid="copy-address-icon-informations-contact"
                  title="Importe l'adresse de rattachement"
                  onClick={handlePasteAdresseRattachement}
                />
              </div>
            }
            saisie="libre"
            handleTypes
            displayDepartement={false}
            adresse={{
              ...adresseContact,
              ...(adresseContact?.libelleCodePostal
                ? { codePostal: adresseContact.libelleCodePostal }
                : {}),
              ...(adresseContact?.libelleCommune
                ? { commune: adresseContact.libelleCommune }
                : {}),
              ...(adresseContact?.libelleVoie
                ? { voie: adresseContact.libelleVoie }
                : {}),
              ...(adresseContact?.libellePays
                ? { pays: adresseContact.libellePays }
                : {}),
            }}
            error={!_.isNil(errors?.adresseContact)}
            onChange={handleAdresseChange}
            onItemChange={handleItemChange}
          />
        </Grid.Column>
        <GridColumnDivider />
        <Grid.Column mobile={16} computer={6} style={{ paddingLeft: 0 }}>
          <CoordonneesContacts
            tel1={tel1}
            tel2={tel2}
            mail={mail}
            header={<SectionHeader title="Coordonnées de contact" />}
            recensement={recensement}
            errors={errors}
            onChange={onChange}
          />
        </Grid.Column>
      </Grid>
    </Section>
  );
};

export default AdresseContact;
